// An athlete's own training data: maxes, and what is shared.
//
// Pure logic. The server owns the rules; this decides how they read, and turns
// the outbox's machine-readable hold reasons into something a person can act on.

/** Validate the record-a-max form. */
export function validateMaxForm(form) {
  const errors = {};

  if (!String(form?.exerciseName ?? '').trim()) {
    errors.exerciseName = 'Choose an exercise.';
  }

  const raw = String(form?.value ?? '').trim();
  if (raw === '') {
    errors.value = 'Enter what you lifted.';
  } else {
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      errors.value = 'That is not a number.';
    } else if (value <= 0) {
      // Unlike a logged set, where zero is bodyweight and negative is assisted.
      // A one-rep max is a real lift.
      errors.value = 'A max is a real lift, so it has to be more than zero.';
    }
  }

  return errors;
}

/** The request body for recording a max. */
export function buildMaxRequest(form, unit) {
  const body = {
    exerciseName: String(form?.exerciseName ?? '').trim(),
    value: Number(form?.value),
    unit,
  };
  const testedOn = String(form?.testedOn ?? '').trim();
  if (testedOn) body.testedOn = testedOn;
  return body;
}

/**
 * Does this logger station prescribe a %1RM the athlete has no max for?
 *
 * The server resolves the percentage against the reader's max and sends the
 * kilograms back as `percentLoad` (routes/programs.go). It sends `percentOneRM`
 * either way. So a percentage with no resolved load behind it — `percentOneRM`
 * present, `percentLoad` absent — is exactly the state where a max is missing,
 * whether or not an absolute load sits behind it as a fallback.
 *
 * This one expression is the whole trigger for the record-a-max affordance:
 *   - has a max      -> `percentLoad` is set        -> false, no control
 *   - bodyweight/etc -> no `percentOneRM`           -> false, no control
 *   - %1RM, no max   -> `percentOneRM` set, no load -> true
 */
export function needsRecordedMax(station) {
  return station?.percentOneRM != null && station?.percentLoad == null;
}

/**
 * What 80% of a max works out to, for a preview beside the input.
 *
 * The server computes the real one against the stored max (MAX-5); this is only
 * a hint while typing, and says so by never being persisted or sent.
 */
export function previewPercent(value, percent) {
  const max = Number(value);
  const pct = Number(percent);
  if (!Number.isFinite(max) || max <= 0 || !Number.isFinite(pct)) return null;
  return Math.round(max * (pct / 100) * 10) / 10;
}

/** The hold reasons the server can send. */
export const HOLD = {
  NO_CONSENT: 'no_consent',
  ASSISTED_LOAD: 'assisted_load_unrepresentable',
  NO_UPSTREAM: 'no_upstream_program',
  NOT_OWN_PROGRAM: 'not_the_athletes_own_program',
};

/**
 * What an event is, in the athlete's words.
 *
 * The server's own vocabulary is a wire format — `performance_observed` names
 * the contract, not the thing that happened. An unknown type falls back to the
 * raw value rather than "Unknown": a slug a reader can search for is more use
 * than a word that admits nothing.
 */
export function eventLabel(eventType) {
  switch (eventType) {
    case 'performance_observed':
      return 'Set logged';
    case 'session_completed':
      return 'Block finished';
    default:
      return eventType || '';
  }
}

/**
 * Explain an outbox row in words an athlete can act on.
 *
 * The reasons are not equivalent, and flattening them to "not sent" would hide
 * the only one they can do anything about.
 *
 * @returns {{text: string, actionable: boolean}}
 */
export function explainEvent(event) {
  if (!event) return { text: '', actionable: false };

  if (event.status === 'sent') {
    return { text: 'Shared with the programme writer.', actionable: false };
  }
  if (event.status === 'pending') {
    return { text: 'Queued to be shared.', actionable: false };
  }
  if (event.status === 'failed') {
    return { text: event.error || 'Could not be shared.', actionable: false };
  }

  switch (event.hold) {
    case HOLD.NO_CONSENT:
      return {
        text: 'Not shared — you had not turned sharing on when this was logged.',
        // The only one the athlete controls.
        actionable: true,
      };
    case HOLD.ASSISTED_LOAD:
      return {
        text: 'Not shared — assisted work cannot be described to the programme '
          + 'writer yet without misreporting it as unassisted. Your set is kept.',
        actionable: false,
      };
    case HOLD.NO_UPSTREAM:
      return {
        text: 'Not shared — this programme was not written by the model, so there '
          + 'is nothing to send it back to.',
        actionable: false,
      };
    case HOLD.NOT_OWN_PROGRAM:
      return {
        text: 'Not shared — this was logged against a shared squad calendar.',
        actionable: false,
      };
    default:
      return { text: 'Not shared.', actionable: false };
  }
}

/**
 * A one-line summary of the outbox, for the panel heading.
 *
 * Distinguishes "sharing is off" from "sharing is on and nothing qualified",
 * because the two look identical in a list of zero sent events.
 */
export function summariseOutbox(events, consent) {
  const rows = events ?? [];
  const shared = rows.filter((e) => e.status === 'sent' || e.status === 'pending').length;
  const held = rows.length - shared;

  if (!consent) {
    return rows.length === 0
      ? 'Sharing is off. Nothing has been shared.'
      : `Sharing is off. ${held} thing${held === 1 ? '' : 's'} held back.`;
  }
  if (rows.length === 0) {
    return 'Sharing is on. Nothing recorded yet.';
  }
  if (shared === 0) {
    // Not "logged sets". A finished block is an event too, and calling it a set
    // miscounts the athlete's training back at them.
    return `Sharing is on, but none of your ${held} record${held === 1 ? '' : 's'} `
      + 'qualified — see below for why.';
  }
  return `Sharing is on. ${shared} shared, ${held} held back.`;
}

/** Format a max for a list row. */
export function formatMax(max) {
  if (!max) return '';
  const tested = max.testedOn ? ` · tested ${max.testedOn}` : '';
  return `${max.value} ${max.unit}${tested}`;
}
